import { Injectable } from "@angular/core";
import { poe2 } from "../../../game";
import { Comparator, Rarity } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Filter, CustomRule, AdvancedConditions } from "../../models/filter-models";
import { RuleGeneratorService } from "./rule-generator.service";
import { ItemDataService } from "../item-data.service";
import { JewelleryTierService } from "../tiering/jewellery-tier.service";
import { JewelleryTierType } from "../../models/item-data-models";

@Injectable({
  providedIn: 'root'
})
export class CraftingBasesGeneratorService {
  constructor(
    private ruleFormatter: RuleGeneratorService,
    private jewelleryTierService: JewelleryTierService,
    private itemDataService: ItemDataService,
  ) {
  }

  public generateExcellentAndGoodJewelleryBaseTypesRules(filter: Filter): string {
    const excellentBaseTypes = this.getCraftingJewelleryBaseTypes('Excellent');
    const goodBaseTypes = this.getCraftingJewelleryBaseTypes('Good');

    const excellentJewelleryBaseTypesRule = filter.showExcellentJewelleryBaseTypes && excellentBaseTypes.length > 0
      ? new CustomRule({
        ...this.poe2JewelleryRarity(),
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showExcellentJewelleryBaseTypesMinItemLevel ?? undefined }),
        baseTypes: excellentBaseTypes,
      }) : undefined;

    const goodJewelleryBaseTypesRule = filter.showGoodJewelleryBaseTypes && goodBaseTypes.length > 0
      ? new CustomRule({
        ...this.poe2JewelleryRarity(),
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showGoodJewelleryBaseTypesMinItemLevel ?? undefined }),
        baseTypes: goodBaseTypes,
      }) : undefined;

    return (excellentJewelleryBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryExcellentBaseTypes, excellentJewelleryBaseTypesRule, filter, 'Excellent Jewellery Base Types') + '\n\n' : '') +
      (goodJewelleryBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryGoodBaseTypes, goodJewelleryBaseTypesRule, filter, 'Good Jewellery Base Types') + '\n\n' : '');
  }

  public generateExcellentAndGoodSpecialJewelleryBaseTypesRules(filter: Filter): string {
    const excellentSpecialBaseTypes = this.getSpecialJewelleryBaseTypesByTier('Excellent');
    const goodSpecialBaseTypes = this.getSpecialJewelleryBaseTypesByTier('Good');

    const excellentSpecialJewelleryBaseTypesRule = filter.showExcellentSpecialJewelleryBaseTypes && excellentSpecialBaseTypes.length > 0
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showExcellentSpecialJewelleryBaseTypesMinItemLevel ?? undefined }),
        baseTypes: excellentSpecialBaseTypes,
      }) : undefined;

    const goodSpecialJewelleryBaseTypesRule = filter.showGoodSpecialJewelleryBaseTypes && goodSpecialBaseTypes.length > 0
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showGoodSpecialJewelleryBaseTypesMinItemLevel ?? undefined }),
        baseTypes: goodSpecialBaseTypes,
      }) : undefined;

    return (excellentSpecialJewelleryBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryExcellentSpecialBaseTypes, excellentSpecialJewelleryBaseTypesRule, filter, 'Excellent Special Jewellery') + '\n\n' : '') +
      (goodSpecialJewelleryBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryGoodSpecialBaseTypes, goodSpecialJewelleryBaseTypesRule, filter, 'Good Special Jewellery') + '\n\n' : '');
  }

  public generateOtherSpecialJewelleryBaseTypesRules(filter: Filter): string {
    const allSpecialBaseTypes = this.itemDataService.getSpecialJewelleryBaseTypes();
    let selectedSpecialJewelleryBaseTypes = (
      !filter.showOtherSpecialJewelleryBaseTypesTypes
      || filter.showOtherSpecialJewelleryBaseTypesTypes.length === 0
      || filter.showOtherSpecialJewelleryBaseTypesTypes.includes('All'))
      ? allSpecialBaseTypes
      : filter.showOtherSpecialJewelleryBaseTypesTypes.filter(bt => allSpecialBaseTypes.includes(bt));

    if (poe2) {
      if (filter.showExcellentSpecialJewelleryBaseTypes) {
        const excellentSpecialSet = new Set(this.getSpecialJewelleryBaseTypesByTier('Excellent'));
        selectedSpecialJewelleryBaseTypes = selectedSpecialJewelleryBaseTypes.filter(bt => !excellentSpecialSet.has(bt));
      }
      if (filter.showGoodSpecialJewelleryBaseTypes) {
        const goodSpecialSet = new Set(this.getSpecialJewelleryBaseTypesByTier('Good'));
        selectedSpecialJewelleryBaseTypes = selectedSpecialJewelleryBaseTypes.filter(bt => !goodSpecialSet.has(bt));
      }
    }

    const otherSpecialJewelleryBaseTypesRule = filter.showOtherSpecialJewelleryBaseTypes && selectedSpecialJewelleryBaseTypes.length > 0
      ? new CustomRule({
        advancedConditions: new AdvancedConditions({ minItemLevel: filter.showOtherSpecialJewelleryBaseTypesMinItemLevel ?? undefined }),
        baseTypes: selectedSpecialJewelleryBaseTypes,
      }) : undefined;

    return (otherSpecialJewelleryBaseTypesRule ? this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryOtherSpecialBaseTypes, otherSpecialJewelleryBaseTypesRule, filter, 'Other Special Jewellery') + '\n\n' : '');
  }

  private getSpecialJewelleryBaseTypesByTier(tier: JewelleryTierType): string[] {
    const specialSet = new Set(this.itemDataService.getSpecialJewelleryBaseTypes());
    return this.jewelleryTierService.getJewelleryTierData()
      .filter(j => j.tier === tier && specialSet.has(j.name))
      .map(j => j.name);
  }

  private getCraftingJewelleryBaseTypes(tier: JewelleryTierType): string[] {
    let baseTypes = this.jewelleryTierService.getJewelleryTierData()
      .filter(j => j.tier === tier)
      .map(j => j.name);

    if (poe2) {
      const rareOnly = new Set(this.itemDataService.getRareOnlyJewellery());
      baseTypes = baseTypes.filter(bt => !rareOnly.has(bt));
    }

    return baseTypes;
  }

  private poe2JewelleryRarity(): Partial<CustomRule> {
    if (!poe2) return {};
    return {
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Normal,
    };
  }
}
